import { useState, useEffect, useRef } from 'react'
import {
  FileText, ExternalLink, RefreshCw, AlertCircle, Search, X, Share2, Printer,
  Copy, ChevronLeft, ChevronRight, MoreVertical, Maximize2, Tag, ChevronDown
} from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../services/apiService'
import Modal from '../components/Modal'

const PAPERLESS_ULTIMO_VISTO_KEY = 'paperless_ultimo_documento_visto'
const ITENS_POR_PAGINA = 12
const MAX_TAGS_NO_CARD = 3
const FILTROS_VAZIOS = { q: '', de: '', ate: '', tags: [] }

function fmtData(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

function temFiltroAplicado(f) {
  return Boolean(f.q || f.de || f.ate || f.tags.length)
}

function TagChip({ tag, onClick, onRemover }) {
  const estilo = { backgroundColor: tag.cor, color: tag.corTexto }
  const classe = 'inline-flex items-center gap-1 max-w-full px-2 py-0.5 text-[11px] font-medium rounded-full truncate'

  if (onRemover) {
    return (
      <span className={classe} style={estilo}>
        <span className="truncate">{tag.nome}</span>
        <button type="button" onClick={onRemover} title="Remover do filtro" className="hover:opacity-70">
          <X size={12} />
        </button>
      </span>
    )
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} title={`Filtrar por "${tag.nome}"`} className={`${classe} hover:opacity-80`} style={estilo}>
        {tag.nome}
      </button>
    )
  }

  return <span className={classe} style={estilo}>{tag.nome}</span>
}

// Tags de um documento: no card mostra só as primeiras, no modal mostra todas
function TagsDocumento({ ids, tagsPorId, limite, onFiltrar }) {
  const tags = (ids || []).map((id) => tagsPorId.get(id)).filter(Boolean)
  if (tags.length === 0) return null

  const visiveis = limite ? tags.slice(0, limite) : tags
  const restantes = tags.slice(visiveis.length)

  return (
    <div className="flex flex-wrap gap-1">
      {visiveis.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          onClick={onFiltrar ? (e) => { e.stopPropagation(); onFiltrar(tag.id) } : undefined}
        />
      ))}
      {restantes.length > 0 && (
        <span
          className="px-1.5 py-0.5 text-[11px] text-gray-500 bg-gray-100 rounded-full"
          title={restantes.map((t) => t.nome).join(', ')}
        >
          +{restantes.length}
        </span>
      )}
    </div>
  )
}

// Seletor de tags (dropdown com busca e checkboxes)
function FiltroTags({ tagsPorId, selecionadas, onChange }) {
  const [aberto, setAberto] = useState(false)
  const [busca, setBusca] = useState('')
  const ref = useRef(null)

  useEffect(() => {
    if (!aberto) return
    const fecharFora = (e) => ref.current && !ref.current.contains(e.target) && setAberto(false)
    const fecharEsc = (e) => e.key === 'Escape' && setAberto(false)
    window.addEventListener('mousedown', fecharFora)
    window.addEventListener('keydown', fecharEsc)
    return () => {
      window.removeEventListener('mousedown', fecharFora)
      window.removeEventListener('keydown', fecharEsc)
    }
  }, [aberto])

  const termo = busca.trim().toLowerCase()
  const lista = [...tagsPorId.values()].filter((t) => !termo || t.nome.toLowerCase().includes(termo))

  function alternar(id) {
    onChange(selecionadas.includes(id) ? selecionadas.filter((t) => t !== id) : [...selecionadas, id])
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        disabled={tagsPorId.size === 0}
        className="w-full md:w-40 inline-flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
      >
        <span className="inline-flex items-center gap-2 text-gray-700">
          <Tag size={16} className="text-gray-400" />
          {selecionadas.length ? `${selecionadas.length} selecionada${selecionadas.length !== 1 ? 's' : ''}` : 'Todas'}
        </span>
        <ChevronDown size={16} className="text-gray-400" />
      </button>

      {aberto && (
        <div className="absolute z-40 right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Procurar tag"
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1">
            {lista.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">Nenhuma tag</p>}
            {lista.map((tag) => (
              <label key={tag.id} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selecionadas.includes(tag.id)}
                  onChange={() => alternar(tag.id)}
                  className="rounded border-gray-300"
                />
                <span className="w-3 h-3 rounded-full shrink-0 border border-gray-200" style={{ backgroundColor: tag.cor }} />
                <span className="flex-1 truncate text-gray-700">{tag.nome}</span>
                {tag.totalDocumentos != null && <span className="text-xs text-gray-400">{tag.totalDocumentos}</span>}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DocumentoThumb({ id, titulo }) {
  const [src, setSrc] = useState(null)

  useEffect(() => {
    let objectUrl
    let cancelado = false
    setSrc(null)

    apiService.paperless.getThumbBlob(id)
      .then(({ data }) => {
        if (cancelado) return
        objectUrl = URL.createObjectURL(data)
        setSrc(objectUrl)
      })
      .catch(() => {})

    return () => {
      cancelado = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [id])

  if (!src) {
    return (
      <div className="w-full aspect-[3/4] bg-gray-100 rounded-lg flex items-center justify-center">
        <FileText className="text-gray-300" size={32} />
      </div>
    )
  }

  return (
    <img
      src={src}
      alt={titulo}
      className="w-full aspect-[3/4] object-cover object-top rounded-lg border border-gray-200"
    />
  )
}

// Números de página exibidos na paginação: 1 … 4 5 [6] 7 8 … 20
function paginasVisiveis(atual, total) {
  const paginas = new Set([1, total])
  for (let p = atual - 2; p <= atual + 2; p++) {
    if (p >= 1 && p <= total) paginas.add(p)
  }
  const ordenadas = [...paginas].sort((a, b) => a - b)
  const resultado = []
  ordenadas.forEach((p, i) => {
    if (i > 0 && p - ordenadas[i - 1] > 1) resultado.push('…')
    resultado.push(p)
  })
  return resultado
}

function Paginacao({ pagina, totalPaginas, onChange, disabled }) {
  if (totalPaginas <= 1) return null

  const btnBase = 'min-w-[36px] h-9 px-2 text-sm rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div className="flex items-center justify-center gap-1 flex-wrap">
      <button
        onClick={() => onChange(pagina - 1)}
        disabled={disabled || pagina <= 1}
        className={`${btnBase} border-gray-200 bg-white hover:bg-gray-50 inline-flex items-center gap-1`}
      >
        <ChevronLeft size={16} /> Mais recentes
      </button>
      {paginasVisiveis(pagina, totalPaginas).map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="px-1 text-gray-400">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            disabled={disabled || p === pagina}
            className={`${btnBase} ${p === pagina
              ? 'bg-blue-600 border-blue-600 text-white disabled:opacity-100'
              : 'border-gray-200 bg-white hover:bg-gray-50'}`}
          >
            {p}
          </button>
        )
      )}
      <button
        onClick={() => onChange(pagina + 1)}
        disabled={disabled || pagina >= totalPaginas}
        className={`${btnBase} border-gray-200 bg-white hover:bg-gray-50 inline-flex items-center gap-1`}
      >
        Mais antigos <ChevronRight size={16} />
      </button>
    </div>
  )
}

// Menu aberto pelo botão direito (ou pelo botão ⋮ do card)
function MenuContexto({ menu, onAbrir, onCompartilhar, onFechar }) {
  useEffect(() => {
    if (!menu) return
    const fechar = () => onFechar()
    const fecharEsc = (e) => e.key === 'Escape' && onFechar()
    window.addEventListener('click', fechar)
    window.addEventListener('scroll', fechar, true)
    window.addEventListener('resize', fechar)
    window.addEventListener('keydown', fecharEsc)
    return () => {
      window.removeEventListener('click', fechar)
      window.removeEventListener('scroll', fechar, true)
      window.removeEventListener('resize', fechar)
      window.removeEventListener('keydown', fecharEsc)
    }
  }, [menu, onFechar])

  if (!menu) return null

  // Evita que o menu saia da tela nas bordas direita/inferior
  const left = Math.min(menu.x, window.innerWidth - 200)
  const top = Math.min(menu.y, window.innerHeight - 100)

  const itemClass = 'w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 text-left'

  return (
    <div
      className="fixed z-50 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
      style={{ left, top }}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button className={itemClass} onClick={() => { onFechar(); onAbrir(menu.doc) }}>
        <Maximize2 size={16} /> Abrir
      </button>
      <button className={itemClass} onClick={() => { onFechar(); onCompartilhar(menu.doc) }}>
        <Share2 size={16} /> Compartilhar
      </button>
    </div>
  )
}

function LinkCompartilhado({ doc, link }) {
  async function copiar() {
    try {
      await navigator.clipboard.writeText(link.url)
      toast.success('Link copiado')
    } catch {
      toast.error('Não foi possível copiar. Selecione o link e copie manualmente.')
    }
  }

  return (
    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
      <p className="text-xs text-blue-800">
        Link público do Paperless (qualquer pessoa com o link pode abrir)
        {link.expiraEm && <> · expira em {fmtData(link.expiraEm)}</>}
      </p>
      <div className="flex gap-2">
        <input
          readOnly
          value={link.url}
          onFocus={(e) => e.target.select()}
          className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-white border border-blue-200 rounded"
        />
        <button
          onClick={copiar}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-100"
        >
          <Copy size={14} />
          Copiar
        </button>
      </div>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${doc.titulo}: ${link.url}`)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline"
      >
        <Share2 size={14} />
        Enviar pelo WhatsApp
      </a>
    </div>
  )
}

export default function ArquivosEscaneados() {
  const [documentos, setDocumentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [pagina, setPagina] = useState(1)
  const [totalPaginas, setTotalPaginas] = useState(1)
  const [total, setTotal] = useState(0)

  // "form" é o que está digitado; "filtros" é o que foi efetivamente aplicado na busca
  const [form, setForm] = useState(FILTROS_VAZIOS)
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS)

  const [menu, setMenu] = useState(null) // { x, y, doc }

  // Modal de visualização (documento em tamanho maior)
  const [aberto, setAberto] = useState(null)
  const [arquivoUrl, setArquivoUrl] = useState(null)
  const [carregandoArquivo, setCarregandoArquivo] = useState(false)
  const viewerRef = useRef(null)

  // Compartilhamento: pode vir do modal de visualização ou do menu de contexto
  const [compartilhando, setCompartilhando] = useState(null) // doc em que o link está sendo gerado
  const [links, setLinks] = useState({}) // { [docId]: { url, expiraEm } }
  const [modalCompartilhar, setModalCompartilhar] = useState(null) // doc

  const [tagsPorId, setTagsPorId] = useState(new Map())

  const temFiltro = temFiltroAplicado(filtros)

  useEffect(() => {
    carregar(filtros, pagina)
  }, [filtros, pagina])

  // Tags carregadas uma vez; se o Paperless falhar aqui, a tela segue funcionando sem elas
  useEffect(() => {
    apiService.paperless.getTags()
      .then(({ data }) => setTagsPorId(new Map((data.tags || []).map((t) => [t.id, t]))))
      .catch(() => {})
  }, [])

  // Libera o blob do documento aberto quando o modal fecha ou troca de documento
  useEffect(() => () => {
    if (arquivoUrl) URL.revokeObjectURL(arquivoUrl)
  }, [arquivoUrl])

  async function carregar(filtrosAtuais, paginaAtual) {
    setLoading(true)
    setErro('')
    try {
      const { data } = await apiService.paperless.buscarDocumentos({
        ...filtrosAtuais,
        page: paginaAtual,
        pageSize: ITENS_POR_PAGINA
      })
      const lista = data.documentos || []
      setDocumentos(lista)
      setTotal(data.total || 0)
      setTotalPaginas(data.totalPaginas || 1)

      // Só marca como "visto" quando o usuário está olhando as mais recentes, sem filtro
      if (!temFiltroAplicado(filtrosAtuais) && paginaAtual === 1 && lista[0]?.criadoEm) {
        localStorage.setItem(PAPERLESS_ULTIMO_VISTO_KEY, lista[0].criadoEm)
      }
    } catch (error) {
      setErro('Não foi possível carregar as digitalizações.')
    } finally {
      setLoading(false)
    }
  }

  function aplicarBusca(e) {
    e.preventDefault()
    if (form.de && form.ate && form.de > form.ate) {
      toast.error('A data inicial é maior que a data final')
      return
    }
    setPagina(1)
    setFiltros({ ...form, q: form.q.trim() })
  }

  function limparBusca() {
    setForm(FILTROS_VAZIOS)
    setPagina(1)
    setFiltros(FILTROS_VAZIOS)
  }

  // Tags aplicam na hora (sem precisar clicar em Buscar); o texto/período digitado continua pendente
  function aplicarTags(tags) {
    setForm((atual) => ({ ...atual, tags }))
    setPagina(1)
    setFiltros((atual) => ({ ...atual, tags }))
  }

  function filtrarPorTag(id) {
    if (!filtros.tags.includes(id)) aplicarTags([...filtros.tags, id])
  }

  function mudarPagina(p) {
    setPagina(p)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  function abrirMenu(e, doc) {
    e.preventDefault()
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY, doc })
  }

  async function abrirDocumento(doc) {
    setAberto(doc)
    setArquivoUrl(null)
    setCarregandoArquivo(true)
    try {
      const { data } = await apiService.paperless.getArquivoBlob(doc.id)
      setArquivoUrl(URL.createObjectURL(data))
    } catch (error) {
      toast.error('Não foi possível carregar o documento')
    } finally {
      setCarregandoArquivo(false)
    }
  }

  function fecharDocumento() {
    setAberto(null)
    setArquivoUrl(null)
  }

  async function gerarLink(doc) {
    if (links[doc.id]) return links[doc.id]
    setCompartilhando(doc.id)
    try {
      const { data } = await apiService.paperless.compartilharDocumento(doc.id)
      const link = { url: data.url, expiraEm: data.expiraEm }
      setLinks((atual) => ({ ...atual, [doc.id]: link }))
      try {
        await navigator.clipboard.writeText(link.url)
        toast.success('Link copiado')
      } catch {
        // Sem permissão de clipboard: o usuário copia pelo campo exibido
      }
      return link
    } catch (error) {
      toast.error('Não foi possível gerar o link de compartilhamento')
      return null
    } finally {
      setCompartilhando(null)
    }
  }

  async function compartilharPeloMenu(doc) {
    setModalCompartilhar(doc)
    await gerarLink(doc)
  }

  function imprimir() {
    try {
      viewerRef.current.contentWindow.focus()
      viewerRef.current.contentWindow.print()
    } catch {
      // Alguns navegadores não permitem imprimir o PDF embutido: abre em nova aba
      window.open(arquivoUrl, '_blank')
    }
  }

  async function abrirPaperless() {
    // Abrir a aba de forma síncrona evita que o navegador bloqueie o pop-up
    const win = window.open('', '_blank')
    try {
      const { data } = await apiService.paperless.getSsoUrl()
      win.location.href = data.url
    } catch (error) {
      win.close()
      toast.error('Não foi possível abrir o Paperless')
    }
  }

  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  const btnAcao = 'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-blue-600" size={22} />
            Arquivos Scaneados
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {temFiltro ? 'Resultado da busca no Paperless' : 'Digitalizações recebidas no Paperless, das mais recentes para as mais antigas'}
          </p>
        </div>
        <button
          onClick={abrirPaperless}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ExternalLink size={16} />
          Abrir Paperless completo
        </button>
      </div>

      <form
        onSubmit={aplicarBusca}
        className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-end"
      >
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Texto</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={form.q}
              onChange={(e) => setForm({ ...form, q: e.target.value })}
              placeholder="Título ou conteúdo do documento"
              className={`${inputClass} pl-9`}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">De</label>
          <input
            type="date"
            value={form.de}
            onChange={(e) => setForm({ ...form, de: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Até</label>
          <input
            type="date"
            value={form.ate}
            onChange={(e) => setForm({ ...form, ate: e.target.value })}
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tags</label>
          <FiltroTags tagsPorId={tagsPorId} selecionadas={form.tags} onChange={aplicarTags} />
        </div>
        <div className="flex gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Search size={16} />
            Buscar
          </button>
          {(temFiltro || temFiltroAplicado(form)) && (
            <button
              type="button"
              onClick={limparBusca}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <X size={16} />
              Limpar
            </button>
          )}
        </div>
        {filtros.tags.length > 0 && (
          <div className="md:col-span-5 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-gray-500">Com as tags:</span>
            {filtros.tags.map((id) => {
              const tag = tagsPorId.get(id)
              if (!tag) return null
              return (
                <TagChip
                  key={id}
                  tag={tag}
                  onRemover={() => aplicarTags(filtros.tags.filter((t) => t !== id))}
                />
              )
            })}
          </div>
        )}
      </form>

      {!loading && !erro && total > 0 && (
        <p className="text-sm text-gray-500">
          {total} documento{total !== 1 ? 's' : ''} · página {pagina} de {totalPaginas}
          <span className="hidden md:inline"> · clique para abrir, botão direito para compartilhar</span>
        </p>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="animate-spin mr-2" size={20} />
          Carregando...
        </div>
      )}

      {!loading && erro && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle size={18} />
          {erro}
        </div>
      )}

      {!loading && !erro && documentos.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          {temFiltro ? 'Nenhum documento encontrado para essa busca.' : 'Nenhuma digitalização encontrada.'}
        </div>
      )}

      {!loading && !erro && documentos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {documentos.map((doc) => (
            <div
              key={doc.id}
              role="button"
              tabIndex={0}
              onClick={() => abrirDocumento(doc)}
              onKeyDown={(e) => e.key === 'Enter' && abrirDocumento(doc)}
              onContextMenu={(e) => abrirMenu(e, doc)}
              className="group relative text-left bg-white rounded-xl border border-gray-200 shadow-sm p-2.5 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all"
            >
              <DocumentoThumb id={doc.id} titulo={doc.titulo} />
              <button
                onClick={(e) => abrirMenu(e, doc)}
                title="Mais opções"
                className="absolute top-4 right-4 p-1 bg-white/90 border border-gray-200 rounded-md text-gray-600 shadow-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:bg-gray-100 transition-opacity"
              >
                <MoreVertical size={16} />
              </button>
              <p className="mt-2 text-sm font-medium text-gray-900 truncate" title={doc.titulo}>
                {doc.titulo}
              </p>
              <p className="text-xs text-gray-500">{fmtData(doc.criadoEm)}</p>
              <div className="mt-1.5">
                <TagsDocumento ids={doc.tags} tagsPorId={tagsPorId} limite={MAX_TAGS_NO_CARD} onFiltrar={filtrarPorTag} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!erro && (
        <Paginacao pagina={pagina} totalPaginas={totalPaginas} onChange={mudarPagina} disabled={loading} />
      )}

      <MenuContexto
        menu={menu}
        onAbrir={abrirDocumento}
        onCompartilhar={compartilharPeloMenu}
        onFechar={() => setMenu(null)}
      />

      {/* Documento em tamanho maior */}
      <Modal isOpen={Boolean(aberto)} onClose={fecharDocumento} title={aberto?.titulo || 'Documento'} size="xl">
        {aberto && (
          <div className="space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="space-y-1.5">
                <p className="text-sm text-gray-500">
                  {fmtData(aberto.criadoEm)}
                  {aberto.numeroPaginas ? ` · ${aberto.numeroPaginas} página${aberto.numeroPaginas !== 1 ? 's' : ''}` : ''}
                </p>
                <TagsDocumento ids={aberto.tags} tagsPorId={tagsPorId} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => gerarLink(aberto)} disabled={compartilhando === aberto.id} className={btnAcao}>
                  {compartilhando === aberto.id ? <RefreshCw className="animate-spin" size={16} /> : <Share2 size={16} />}
                  Compartilhar
                </button>
                <button onClick={imprimir} disabled={!arquivoUrl} className={btnAcao}>
                  <Printer size={16} />
                  Imprimir
                </button>
                <button onClick={() => window.open(arquivoUrl, '_blank')} disabled={!arquivoUrl} className={btnAcao}>
                  <ExternalLink size={16} />
                  Nova aba
                </button>
              </div>
            </div>

            {links[aberto.id] && <LinkCompartilhado doc={aberto} link={links[aberto.id]} />}

            <div className="h-[70vh] bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
              {carregandoArquivo && (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <RefreshCw className="animate-spin mr-2" size={20} />
                  Carregando documento...
                </div>
              )}
              {!carregandoArquivo && arquivoUrl && (
                <iframe ref={viewerRef} src={arquivoUrl} title={aberto.titulo} className="w-full h-full" />
              )}
              {!carregandoArquivo && !arquivoUrl && (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <AlertCircle className="mr-2" size={20} />
                  Não foi possível carregar o documento.
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Compartilhar direto pelo menu de contexto, sem abrir o documento */}
      <Modal isOpen={Boolean(modalCompartilhar)} onClose={() => setModalCompartilhar(null)} title="Compartilhar documento" size="sm">
        {modalCompartilhar && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-900 break-words">{modalCompartilhar.titulo}</p>
            {compartilhando === modalCompartilhar.id && (
              <div className="flex items-center text-sm text-gray-500">
                <RefreshCw className="animate-spin mr-2" size={16} />
                Gerando link...
              </div>
            )}
            {links[modalCompartilhar.id] && (
              <LinkCompartilhado doc={modalCompartilhar} link={links[modalCompartilhar.id]} />
            )}
            {compartilhando !== modalCompartilhar.id && !links[modalCompartilhar.id] && (
              <button onClick={() => gerarLink(modalCompartilhar)} className={btnAcao}>
                <RefreshCw size={16} />
                Tentar novamente
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
